import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import * as Menu from "@radix-ui/react-dropdown-menu";
import { ChevronDown, LogOut, Search } from "lucide-react";
import { useSessao } from "@/sessao/useSessao";
import { rotaDosBoletos } from "@/rotas";
import { ListrasDeAmbiente, SeloDeAmbiente } from "./SeloDeAmbiente";

/*
 * Cabeçalho de 52px com marca, busca única, selo de ambiente e menu do operador
 * (doc 06b §3).
 *
 * A busca é uma só porque quem liga para o suporte diz o número que tem na mão, e
 * qual dos cinco é depende de quem liga (doc 06 §4.2). O exemplo do campo promete só
 * o que `ClassificarBusca` procura: `trace_id` saiu, porque a API não busca por ele.
 */
export function Cabecalho() {
  const { sair, papel, estado } = useSessao();
  const operadorID = estado.fase === "dentro" ? estado.operadorID : "";

  return (
    <header className="relative flex h-[var(--altura-cabecalho)] shrink-0 items-center gap-3 border-b border-fio bg-superficie px-3">
      <ListrasDeAmbiente />

      <span className="relative z-10 font-semibold text-texto">Boletos Maker</span>

      <BuscaGlobal />

      <div className="relative z-10 flex items-center gap-3">
        <SeloDeAmbiente />

        <Menu.Root>
          <Menu.Trigger className="flex h-8 items-center gap-1 rounded-controle px-2 text-corpo text-texto-2 hover:bg-superficie-2 hover:text-texto">
            <span className="rotulo">{papel ?? "sem papel"}</span>
            <ChevronDown aria-hidden="true" className="size-3.5" />
          </Menu.Trigger>

          <Menu.Portal>
            <Menu.Content
              align="end"
              sideOffset={6}
              className="min-w-[220px] rounded-cartao border border-fio bg-superficie p-1 shadow-none"
            >
              <div className="px-2 py-1.5">
                <div className="rotulo">Operador</div>
                {/* O identificador é UUID: monoespaçado, como todo identificador. */}
                <div className="dados mt-[2px] truncate text-[12px] text-texto-2">
                  {operadorID}
                </div>
              </div>

              <Menu.Separator className="my-1 h-px bg-fio" />

              <Menu.Item
                onSelect={() => void sair()}
                className="flex cursor-pointer items-center gap-2 rounded-controle px-2 py-1.5 text-corpo text-texto outline-none data-[highlighted]:bg-superficie-2"
              >
                <LogOut aria-hidden="true" className="size-4" />
                Sair
              </Menu.Item>
            </Menu.Content>
          </Menu.Portal>
        </Menu.Root>
      </div>
    </header>
  );
}

/*
 * A busca abre Boletos do zero: filtro que sobrou da lista esconderia o boleto procurado,
 * e quem refina depois tem a caixa da própria tela, já com o termo.
 *
 * O termo pode ser CPF. Ele sai do campo ao buscar, para não ficar à vista em toda tela, e
 * `autoComplete` desligado o mantém fora do histórico de formulário do navegador. Na URL ele
 * precisa ir: é o que torna a lista comunicável, e o `access_log` é a pré-condição 13.13.
 */
function BuscaGlobal() {
  const navegar = useNavigate();
  const [texto, setTexto] = useState("");

  function buscar(evento: FormEvent) {
    evento.preventDefault();

    const q = texto.trim();
    if (q === "") return;

    setTexto("");
    void navegar(rotaDosBoletos({ q }));
  }

  return (
    <form role="search" onSubmit={buscar} className="relative z-10 flex-1">
      <div className="relative max-w-[420px]">
        <Search
          aria-hidden="true"
          className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-texto-3"
        />
        <input
          type="search"
          autoComplete="off"
          value={texto}
          onChange={(evento) => setTexto(evento.target.value)}
          placeholder="Referência, nosso número, documento, id, provider_id"
          aria-label="Busca global"
          className="h-8 w-full rounded-controle border border-fio bg-superficie-2 pl-8 pr-3 text-corpo text-texto placeholder:text-texto-3"
        />
      </div>
    </form>
  );
}
